import { type ReactNode } from "react";

import { Sheet } from "./Sheet";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  error?: string | null;
  /** If true, a comment textarea is shown */
  withComment?: boolean;
  comment?: string;
  onCommentChange?: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  danger = false,
  loading = false,
  error,
  withComment,
  comment,
  onCommentChange,
  onConfirm,
  onCancel,
  children,
}: ConfirmModalProps) {
  return (
    <Sheet
      open={open}
      onClose={onCancel}
      title={
        <span style={{ color: danger ? "var(--error)" : "var(--text-primary)" }}>
          {title}
        </span>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: "1px solid var(--border)",
              background: "var(--bg-tertiary)",
              color: "var(--text-secondary)",
              fontSize: 11.5,
              fontWeight: 600,
              cursor: "pointer",
              minHeight: "var(--tap-min)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: danger
                ? "1px solid rgba(239,68,68,0.5)"
                : "1px solid var(--accent)",
              background: danger ? "rgba(239,68,68,0.12)" : "var(--accent)",
              color: danger ? "var(--error)" : "white",
              fontSize: 11.5,
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
              minHeight: "var(--tap-min)",
            }}
          >
            {loading ? "Working…" : confirmLabel}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14, fontFamily: "var(--font-sans)" }}>
        {description && (
          <p style={{ margin: 0, fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
            {description}
          </p>
        )}

        {children}

        {withComment && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-muted)", fontWeight: 600 }}>
              Comment (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => onCommentChange?.(e.target.value)}
              rows={3}
              placeholder="Reason for this transition…"
              style={{
                background: "var(--bg-input)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "8px 10px",
                color: "var(--text-primary)",
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                resize: "vertical",
                outline: "none",
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          </div>
        )}

        {error && (
          <div style={{
            background: "rgba(239,68,68,0.10)",
            border: "1px solid rgba(239,68,68,0.35)",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 11.5,
            color: "var(--error)",
          }}>
            {error}
          </div>
        )}
      </div>
    </Sheet>
  );
}
